import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ArrowRight, UserPlus } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name"),
  email: z.string().trim().email("Enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const FIELD_CLASS =
  "w-full rounded-xl border border-navy-900/15 bg-white px-4 py-3 text-sm text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState("");
  const redirect = searchParams.get("redirect") || "/account";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { name: "", email: "", password: "" } });

  const onSubmit = async (values) => {
    setServerError("");
    try {
      await signup(values.name, values.email, values.password);
      navigate(redirect, { replace: true });
    } catch (err) {
      setServerError(err.message);
    }
  };

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="signup-page">
      <SEO
        title="Create Account | Biz360 Compliance"
        canonical="/signup"
        robots="noindex, nofollow"
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-md px-5 lg:px-8">
            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_16px_40px_rgba(15,30,60,0.08)]" data-testid="signup-card">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-navy-900 text-gold-400">
                  <UserPlus className="h-5 w-5" />
                </span>
                <h1 className="mt-5 text-2xl font-bold text-navy-900">Create your account</h1>
                <p className="mt-2 text-sm text-slate-600">Sign up to upload documents and track your submissions with our team.</p>

                <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-5" data-testid="signup-form" noValidate>
                  {serverError && (
                    <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="signup-error">
                      {serverError}
                    </p>
                  )}
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-semibold text-navy-800">Full Name</label>
                    <input id="name" type="text" placeholder="Ananya Sharma" className={FIELD_CLASS} data-testid="signup-input-name" {...register("name")} />
                    {errors.name && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-navy-800">Email Address</label>
                    <input id="email" type="email" placeholder="you@company.com" className={FIELD_CLASS} data-testid="signup-input-email" {...register("email")} />
                    {errors.email && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-navy-800">Password</label>
                    <input id="password" type="password" placeholder="At least 6 characters" className={FIELD_CLASS} data-testid="signup-input-password" {...register("password")} />
                    {errors.password && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.password.message}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="signup-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold-500 px-7 py-3.5 text-sm font-semibold text-navy-950 shadow-[0_12px_30px_rgba(212,162,76,0.35)] transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
                  >
                    {isSubmitting ? "Creating account..." : "Sign Up"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>

                <p className="mt-6 text-center text-sm text-slate-600">
                  Already have an account?{" "}
                  <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="font-semibold text-gold-600 hover:underline" data-testid="signup-login-link">
                    Log in
                  </Link>
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
